import React from 'react';
import type { GridColDef} from '@mui/x-data-grid';
import { formatDate } from '../../dashboard/components/dateUtils';
import { parseRawFormData } from '../../dashboard/components/formUtils';
import { HandlersTarResponse } from '../../app/athenaApiGen';
import {
  getWorkflowProgress,
  WorkflowProgressValue,
} from '../../utils/workflow';

// Extended type to handle rawFormData parsing.
// `subject`, `projectName`, `totalNTETripCost` are sourced at runtime from
// either the rawFormData blob or back-end-resolved display fields, but the
// regenerated HandlersTarResponse no longer declares them — keep them on the
// local view type so the column mappings type-check.
type TARApiItem = HandlersTarResponse & {
  travelDate?: string;
  workflowState?: string;
  workflowProgress?: WorkflowProgressValue;
  subject?: string;
  projectName?: string;
  totalNTETripCost?: number;
};

export interface TARRowData {
  id: string;
  tarNumber: string;
  travelDate: string | null;
  projectName: string;
  subject: string;
  totalNTETripCost: number | null;
  workflowState: string | null;
  workflowProgress: WorkflowProgressValue;
  workflowId: string | null;
}

const usdWithCents = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const FALLBACK_TEXT = '--';

/**
 * Returns the first non-empty string from a list of API/raw form values.
 */
const pickFirstString = (
  ...values: Array<string | null | undefined>
): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
  }
  return undefined;
};

/**
 * Coerces a value into a finite number or null when parsing fails.
 */
const toNumberOrNull = (value: any): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Builds the TAR list DataGrid column definitions with formatting helpers.
 */
export const createTARColumns = (): GridColDef<TARRowData>[] => [
  { field: 'tarNumber', headerName: 'TAR #', minWidth: 140, flex: 0.6 },
  {
    field: 'travelDate',
    headerName: 'Travel Date',
    minWidth: 150,
    valueGetter: (_value, row) => row.travelDate ?? null,
    valueFormatter: (value) => formatDate(value as string | null | undefined),
    sortable: false,
    flex: 0.6,
  },
  { field: 'projectName', headerName: 'Project', minWidth: 220, flex: 1 },
  { field: 'subject', headerName: 'Subject', minWidth: 220, flex: 1 },
  {
    field: 'totalNTETripCost',
    headerName: 'Est. Total',
    minWidth: 140,
    flex: 0.6,
    valueGetter: (_value, row) => {
      // Ensure we preserve decimal precision from the row data
      const rawValue = row.totalNTETripCost;
      if (rawValue === null || rawValue === undefined) return null;
      // Parse as float to preserve decimal places
      const numeric = typeof rawValue === 'number' ? rawValue : parseFloat(String(rawValue));
      return Number.isFinite(numeric) ? numeric : null;
    },
    valueFormatter: (value) => {
      if (value === null || value === undefined) return FALLBACK_TEXT;
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return FALLBACK_TEXT;
      return usdWithCents.format(numeric);
    },
  },
  {
    field: 'workflowState',
    headerName: 'Status',
    minWidth: 200,
    flex: 0.9,
    sortable: false,
    valueGetter: (value: string) => value ?? '-',
  },
];

/**
 * Normalizes TAR API responses into grid-ready row objects.
 */
export const mapTARApiDataToRows = (items: TARApiItem[]): TARRowData[] =>
  items.map((it) => {
    const raw = parseRawFormData(it.rawFormData);
    const workflowState =
      pickFirstString(
        it.workflowState as string | undefined,
        raw.workflowState,
        raw?.workflow?.state,
        raw?.workflowStateLabel,
      ) || null;
    const workflowProgress =
      (it.workflowProgress as WorkflowProgressValue | undefined) ??
      (raw.workflowProgress as WorkflowProgressValue | undefined) ??
      getWorkflowProgress(workflowState ?? undefined);

    return {
      id: it.tarId ?? '',
      tarNumber: pickFirstString(it.tarNumber, raw.tarNumber) ?? FALLBACK_TEXT,
      travelDate:
        pickFirstString(
          it.travelDate,
          raw.travelDate,
          raw?.travelDates?.start,
          raw?.travelDates?.from,
          raw?.tarTravelDates?.start,
        ) || null,
      projectName: pickFirstString(it.projectName, raw.projectName) ?? FALLBACK_TEXT,
      subject: pickFirstString(it.subject, raw.subject) ?? FALLBACK_TEXT,
      totalNTETripCost: toNumberOrNull(
        it.totalNTETripCost ??
          raw.totalNTETripCost ??
          raw.totalCost ??
          raw.totalCostOfTrip ??
          raw.tripTotal,
      ),
      workflowState,
      workflowProgress,
      workflowId: it.workflowId ?? null,
    };
  });
